import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";

import AgriPanel from "components/AgriTheme/AgriPanel";
import AgriStatCard from "components/AgriTheme/AgriStatCard";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import UserIdentityPreview from "components/UserIdentityPreview";
import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const PERIODS = [
  { value: "day", label: "Day" },
  { value: "date_range", label: "Date Range" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];
const PAGE_SIZES = [10, 25, 50, 100];
const TEST_STATUSES = ["draft", "scheduled", "live", "completed", "cancelled"];

const getCurrentValues = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return {
    day: `${year}-${month}-${day}`,
    week: `${year}-${month}-${day}`,
    month: `${year}-${month}`,
    year: String(year),
  };
};

const getInputType = (period) => {
  if (period === "month") return "month";
  if (period === "year") return "number";
  return "date";
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getUserImageUrl = (value) => {
  if (!value || String(value).startsWith("http")) return value || "";
  return `${Constants.imageDomain}${value}`;
};

const statusSx = (status) => {
  const colors = {
    draft: ["#94a3b8", "rgba(148,163,184,0.13)"],
    scheduled: ["#38bdf8", "rgba(56,189,248,0.13)"],
    live: ["#22c55e", "rgba(34,197,94,0.13)"],
    completed: ["#a78bfa", "rgba(167,139,250,0.13)"],
    cancelled: ["#fb7185", "rgba(251,113,133,0.13)"],
    invited: ["#fbbf24", "rgba(251,191,36,0.13)"],
    accepted: ["#38bdf8", "rgba(56,189,248,0.13)"],
    joined: ["#a78bfa", "rgba(167,139,250,0.13)"],
    attempted: ["#22c55e", "rgba(34,197,94,0.13)"],
  };
  const [color, backgroundColor] = colors[status] || colors.draft;
  return { color, backgroundColor, border: `1px solid ${color}66`, fontWeight: 700 };
};

function FriendsTestReports() {
  const currentValues = useMemo(getCurrentValues, []);
  const initialFilters = useMemo(
    () => ({
      period: "month",
      value: currentValues.month,
      start_date: currentValues.day,
      end_date: currentValues.day,
      status: "all",
      search: "",
    }),
    [currentValues]
  );
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [inviteRowsPerPage, setInviteRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState({ summary: {}, docs: [], pagination: {}, range: {} });
  const [inviteDialog, setInviteDialog] = useState({ open: false, loading: false, data: null });
  const [notification, setNotification] = useState({ open: false, message: "" });

  useEffect(() => {
    if (init() !== "success") return undefined;

    setLoading(true);
    axios
      .get(Constants.getUrls.getFriendsTestReports, {
        params: {
          ...appliedFilters,
          start_date: appliedFilters.period === "date_range" ? appliedFilters.start_date : undefined,
          end_date: appliedFilters.period === "date_range" ? appliedFilters.end_date : undefined,
          page,
          limit: rowsPerPage,
        },
      })
      .then((response) => setReport(response?.data?.data || {}))
      .catch((error) =>
        setNotification({
          open: true,
          message: error?.response?.data?.message || "Unable to load friends test reports.",
        })
      )
      .finally(() => setLoading(false));

    return undefined;
  }, [appliedFilters, page, rowsPerPage]);

  const loadInvites = (test, invitePage = 1, inviteLimit = inviteRowsPerPage) => {
    setInviteDialog((current) => ({
      open: true,
      loading: true,
      data: current.data?.test?._id === test._id ? current.data : { test, docs: [], pagination: {} },
    }));
    axios
      .get(`${Constants.getUrls.getFriendsTestInvites}${test._id}/invites`, {
        params: { page: invitePage, limit: inviteLimit },
      })
      .then((response) =>
        setInviteDialog({ open: true, loading: false, data: response?.data?.data || null })
      )
      .catch((error) => {
        setInviteDialog((current) => ({ ...current, loading: false }));
        setNotification({
          open: true,
          message: error?.response?.data?.message || "Unable to load invited users.",
        });
      });
  };

  const handlePeriodChange = (period) => {
    setFilters((current) => ({
      ...current,
      period,
      value: period === "date_range" ? current.value : currentValues[period],
    }));
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const summary = report?.summary || {};
  const pagination = report?.pagination || {};

  return (
    <DashboardLayout>
      <DashboardNavbar title="Friends Test Reports" />

      <MDBox mb={2} display="flex" gap={1.25} flexWrap="wrap" alignItems="center">
        <TextField
          select
          size="small"
          label="View By"
          value={filters.period}
          onChange={(event) => handlePeriodChange(event.target.value)}
          sx={{ minWidth: 140 }}
        >
          {PERIODS.map((item) => (
            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
          ))}
        </TextField>
        {filters.period === "date_range" ? (
          <>
            <TextField
              size="small"
              type="date"
              label="From"
              value={filters.start_date}
              onChange={(event) => setFilters((current) => ({ ...current, start_date: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: filters.end_date }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={filters.end_date}
              onChange={(event) => setFilters((current) => ({ ...current, end_date: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filters.start_date }}
            />
          </>
        ) : (
          <TextField
            size="small"
            type={getInputType(filters.period)}
            label={PERIODS.find((item) => item.value === filters.period)?.label}
            value={filters.value}
            onChange={(event) => setFilters((current) => ({ ...current, value: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            inputProps={filters.period === "year" ? { min: 2020, max: Number(currentValues.year) } : {}}
          />
        )}
        <TextField
          select
          size="small"
          label="Status"
          value={filters.status}
          onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
          sx={{ minWidth: 135 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          {TEST_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Test or creator"
          value={filters.search}
          onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
          onKeyDown={(event) => { if (event.key === "Enter") applyFilters(); }}
          sx={{ minWidth: 210 }}
        />
        <MDButton color="info" size="small" onClick={applyFilters}>Apply</MDButton>
        <MDTypography variant="caption" sx={{ color: "#8fa7c4" }}>
          {report?.range?.start_date || "-"} to {report?.range?.end_date || "-"}
        </MDTypography>
      </MDBox>

      <Grid container spacing={1.25} mb={2.5}>
        {[
          ["quiz", "Tests Created", summary.tests_created || 0, "linear-gradient(135deg,#0ea5e9,#075985)"],
          ["person", "Creators", summary.unique_creators || 0, "linear-gradient(135deg,#8b5cf6,#4c1d95)"],
          ["group_add", "Invited", summary.total_invited || 0, "linear-gradient(135deg,#f59e0b,#92400e)"],
          ["how_to_reg", "Accepted", summary.total_accepted || 0, "linear-gradient(135deg,#06b6d4,#164e63)"],
          ["login", "Joined", summary.total_joined || 0, "linear-gradient(135deg,#22c55e,#14532d)"],
          ["task_alt", "Attempted", summary.total_attempted || 0, "linear-gradient(135deg,#f43f5e,#881337)"],
        ].map(([icon, label, value, tone]) => (
          <Grid item xs={6} md={4} xl={2} key={label}>
            <AgriStatCard icon={icon} label={label} value={value} tone={tone} compact dense />
          </Grid>
        ))}
      </Grid>

      <AgriPanel
        title="Created Friends Tests"
        subtitle="Creator and invite progress for each test. Contact information is hidden."
        noPadding
      >
        <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
          <Table
            size="small"
            sx={{
              minWidth: 1320,
              tableLayout: "fixed",
              "& .MuiTableCell-root": {
                color: "#c7d8eb",
                borderBottom: "1px solid rgba(126,170,220,0.1)",
                fontSize: "0.72rem",
                padding: "10px 11px",
                verticalAlign: "middle",
              },
              "& .MuiTableHead-root": { display: "table-header-group", padding: 0 },
              "& .MuiTableHead-root .MuiTableCell-root": {
                color: "#fff",
                fontWeight: 700,
                background: "rgba(17,35,56,0.94)",
                whiteSpace: "nowrap",
              },
            }}
          >
            <colgroup>
              <col style={{ width: 55 }} /><col style={{ width: 220 }} /><col style={{ width: 190 }} />
              <col style={{ width: 135 }} /><col style={{ width: 165 }} /><col style={{ width: 105 }} />
              <col style={{ width: 80 }} /><col style={{ width: 75 }} /><col style={{ width: 80 }} />
              <col style={{ width: 70 }} /><col style={{ width: 85 }} /><col style={{ width: 125 }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell><TableCell>Test</TableCell><TableCell>Creator</TableCell>
                <TableCell>Created</TableCell><TableCell>Schedule</TableCell><TableCell>Status</TableCell>
                <TableCell align="center">Questions</TableCell><TableCell align="center">Invited</TableCell>
                <TableCell align="center">Accepted</TableCell><TableCell align="center">Joined</TableCell>
                <TableCell align="center">Attempted</TableCell><TableCell align="center">Invitees</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={12}>Loading friends test reports...</TableCell></TableRow>
              ) : !report?.docs?.length ? (
                <TableRow><TableCell colSpan={12}>No friends tests found for this period.</TableCell></TableRow>
              ) : report.docs.map((test, index) => (
                <TableRow key={test._id} hover>
                  <TableCell>{(pagination.page - 1) * pagination.limit + index + 1}</TableCell>
                  <TableCell>
                    <MDTypography variant="button" fontWeight="bold" sx={{ color: "#fff" }}>{test.title}</MDTypography>
                    <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                      {[...(test.subjects || []), ...(test.topics || [])].slice(0, 2).join(" • ") || "-"}
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    {test.creator?._id ? (
                      <Link to={`/user-details/${test.creator._id}`} style={{ textDecoration: "none" }}>
                        <UserIdentityPreview
                          name={test.creator.name}
                          imageUrl={getUserImageUrl(test.creator.profile_pic_url)}
                          imageSize={34}
                        />
                      </Link>
                    ) : test.creator?.name || "Unknown User"}
                    <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                      {test.creator?.college || "-"}
                    </MDTypography>
                  </TableCell>
                  <TableCell>{formatDateTime(test.createdAt)}</TableCell>
                  <TableCell>{formatDateTime(test.start_time)}</TableCell>
                  <TableCell><Chip size="small" label={test.test_status} sx={statusSx(test.test_status)} /></TableCell>
                  <TableCell align="center">{test.total_questions}</TableCell>
                  <TableCell align="center">{test.invite_counts?.invited || 0}</TableCell>
                  <TableCell align="center">{test.invite_counts?.accepted || 0}</TableCell>
                  <TableCell align="center">{test.invite_counts?.joined || 0}</TableCell>
                  <TableCell align="center">{test.invite_counts?.attempted || 0}</TableCell>
                  <TableCell align="center">
                    <MDButton variant="outlined" color="info" size="small" onClick={() => loadInvites(test)}>
                      View
                    </MDButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <MDBox px={2} py={1.5} display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <MDBox display="flex" alignItems="center" gap={1.25}>
            <MDTypography variant="caption" sx={{ color: "#8fa7c4" }}>Rows per page</MDTypography>
            <TextField
              select size="small" value={rowsPerPage}
              onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }}
              sx={{ width: 78 }}
            >
              {PAGE_SIZES.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
            </TextField>
            <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
              {pagination.totalDocs
                ? `${(pagination.page - 1) * pagination.limit + 1}-${Math.min(pagination.page * pagination.limit, pagination.totalDocs)} of ${pagination.totalDocs}`
                : "0 records"}
            </MDTypography>
          </MDBox>
          {pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page} count={pagination.totalPages} variant="outlined" shape="rounded"
              sx={agriPaginationSx} onChange={(_, nextPage) => setPage(nextPage)}
            />
          ) : null}
        </MDBox>
      </AgriPanel>

      <Dialog
        open={inviteDialog.open}
        onClose={() => setInviteDialog({ open: false, loading: false, data: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { background: "#0b1624", color: "#fff", border: "1px solid rgba(126,170,220,0.18)" } }}
      >
        <DialogTitle sx={{ color: "#fff", fontWeight: 700 }}>
          Invited Users • {inviteDialog.data?.test?.title || "Friends Test"}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "rgba(126,170,220,0.14)" }}>
          {inviteDialog.loading ? (
            <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>Loading invited users...</MDTypography>
          ) : !inviteDialog.data?.docs?.length ? (
            <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>No invited users found.</MDTypography>
          ) : (
            <MDBox display="grid" gap={1}>
              {inviteDialog.data.docs.map((invite) => (
                <MDBox
                  key={invite._id} p={1.25} borderRadius="lg" display="flex" alignItems="center"
                  justifyContent="space-between" gap={2}
                  sx={{ background: "rgba(17,35,56,0.78)", border: "1px solid rgba(126,170,220,0.12)" }}
                >
                  <MDBox minWidth={0}>
                    {invite.user_id ? (
                      <Link to={`/user-details/${invite.user_id}`} style={{ textDecoration: "none" }}>
                        <UserIdentityPreview
                          name={invite.name}
                          imageUrl={getUserImageUrl(invite.profile_pic_url)}
                          imageSize={36}
                        />
                      </Link>
                    ) : (
                      <MDTypography variant="button" fontWeight="bold" sx={{ color: "#fff" }}>{invite.name}</MDTypography>
                    )}
                    <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                      {invite.college || "Not registered in AgriQuiz"}
                    </MDTypography>
                  </MDBox>
                  <Chip size="small" label={invite.invite_status} sx={statusSx(invite.invite_status)} />
                </MDBox>
              ))}
            </MDBox>
          )}
          {inviteDialog.data ? (
            <MDBox
              mt={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
            >
              <MDBox display="flex" alignItems="center" gap={1.25}>
                <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
                  Rows per page
                </MDTypography>
                <TextField
                  select
                  size="small"
                  value={inviteRowsPerPage}
                  onChange={(event) => {
                    const nextLimit = Number(event.target.value);
                    setInviteRowsPerPage(nextLimit);
                    loadInvites(inviteDialog.data.test, 1, nextLimit);
                  }}
                  sx={{ width: 82 }}
                >
                  {PAGE_SIZES.map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </TextField>
              </MDBox>
              {inviteDialog.data?.pagination?.totalPages > 1 ? (
              <Pagination
                page={inviteDialog.data.pagination.page}
                count={inviteDialog.data.pagination.totalPages}
                variant="outlined" shape="rounded" sx={agriPaginationSx}
                onChange={(_, nextPage) => loadInvites(inviteDialog.data.test, nextPage)}
              />
              ) : null}
            </MDBox>
          ) : null}
        </DialogContent>
      </Dialog>

      <MDSnackbar
        color="warning" icon="notifications" title="AgriQuiz" content={notification.message}
        dateTime="" open={notification.open}
        onClose={() => setNotification({ open: false, message: "" })}
        close={() => setNotification({ open: false, message: "" })} bgWhite
      />
    </DashboardLayout>
  );
}

export default FriendsTestReports;
